import Link from "next/link";
import take from "ramda/src/take";
import { GetProductQuery, Guide } from "../../../../../graphql/operations";
import { useAnalyticsTracker } from "../../../../Atoms/Analytics";
import { CloudinaryImage } from "../../../../Atoms/Cloudinary";
import { ConditionalWrapper } from "../../../../Atoms/ConditionalWrapper";
import ReadMore from "../../../../Atoms/ReadMore";
import { Text } from "../../../../Atoms/Typography";
import styles from "./CreatorExcerpt.module.css";
import ProfileHeads from "./ProfileHeads";

type FeaturedCommentProps = {
  text: string;
  canExpand?: boolean;
};
function FeaturedComment({ text, canExpand }: FeaturedCommentProps) {
  const tracker = useAnalyticsTracker();
  const lines = text.split("\n");

  function handleExpand() {
    tracker.trackEvent({
      category: "detail",
      action: "expand",
      label: "owner-comment",
    });
  }

  return (
    <ConditionalWrapper
      condition={canExpand}
      wrapper={(children) => (
        <ReadMore onExpand={handleExpand}>{children}</ReadMore>
      )}
    >
      <p>
        {lines.map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
      </p>
    </ConditionalWrapper>
  );
}

export type CreatorExcerptProps = {
  data: { product: GetProductQuery["product"]; guides?: Guide[] };
  canExpand?: boolean;
};

export default function CreatorExcerpt({
  data,
  canExpand,
}: CreatorExcerptProps) {
  const { product, guides } = data;
  const { featuredGuide } = product;
  const { featuredGuideComment } = product.translation;
  const tracker = useAnalyticsTracker();
  const firstThreeGuides: any =
    guides && guides.length > 0
      ? take(
          3,
          guides.filter((g) => !!g.profilePic)
        )
      : [];

  return (
    <div>
      <div className={styles.ProductCreatorAndGuides}>
        <div style={{ paddingBottom: 20 }}>
          <div>
            <Text size="large" bold>
              Why {featuredGuide.nickName} Recommends This Tour
            </Text>
          </div>
          <p>
            <Text size="smaller">
              Tips and recommendations by our official guides
            </Text>
          </p>
        </div>

        <div>
          <FeaturedComment text={featuredGuideComment} canExpand={canExpand} />

          <div className={styles.DetailPartsOwnerPeople}>
            <div className={styles.DetailPartsOwnerProfile}>
              <Link
                title={`${featuredGuide.nickName} profile page`}
                href={`/guide-profile/${featuredGuide.id}?ref=detail-owner`}
                className={styles.HeadContainer}
              >
                {featuredGuide.profilePic && (
                  <CloudinaryImage
                    transformations={["w_64", "h_64", "c_fill"]}
                    src={featuredGuide.profilePic}
                  />
                )}
              </Link>
              <div className={styles.DetailPartsOwnerProfileText}>
                <Text size="normal" bold>
                  {featuredGuide.nickName}
                </Text>
                <p>Official guide</p>
              </div>
            </div>

            <div className={styles.DetailPartsOwnerMoreGuides}>
              {guides && guides.length > 0 ? (
                <ProfileHeads guides={firstThreeGuides} />
              ) : null}

              <span style={{ marginLeft: 16 }}>
                <Link
                  href={`/guide-listing/${product.id}`}
                  target="_blank"
                  onClick={() =>
                    tracker.trackEvent({
                      category: "onpage",
                      action: "click-view-all-guides",
                      label: "detail::owner::view-all-guides",
                    })
                  }
                  className="ui-Link"
                >
                  View all guides
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
